import { ref, computed } from 'vue'
import type { ThemeCommonVars, SelectProps, AutoCompleteProps, DataTableProps } from 'naive-ui'
import type { PopoverThemeVars } from 'naive-ui/es/popover/styles'
import type { DropdownThemeVars } from 'naive-ui/es/dropdown/styles'
import type { TagThemeVars } from 'naive-ui/es/tag/styles'
import type { InputThemeVars } from 'naive-ui/es/input/styles'
import type { ButtonThemeVars } from 'naive-ui/es/button/styles'
import type { RateThemeVars } from 'naive-ui/es/rate/styles'
import type { ModalThemeVars } from 'naive-ui/es/modal/styles'
import type { CardThemeVars } from 'naive-ui/es/card/styles'
import type { FormThemeVars } from 'naive-ui/es/form/styles'
import type { InternalSelectionThemeVars } from 'naive-ui/es/_internal/selection/styles'
import type { DataTableThemeVars } from 'naive-ui/es/data-table/styles'
import type { LayoutThemeVars } from 'naive-ui/es/layout/styles'
import type { CollapseThemeVars } from 'naive-ui/es/collapse/styles'
import type { DividerThemeVars } from 'naive-ui/es/divider/styles'
import type { IconThemeVars } from 'naive-ui/es/icon/styles'
import type { IconWrapperThemeVars } from 'naive-ui/es/icon-wrapper/styles'
import type { PageHeaderThemeVars } from 'naive-ui/es/page-header/styles'
import type { TypographyThemeVars } from 'naive-ui/es/typography/styles'
import type { SelectThemeVars } from 'naive-ui/es/select/styles'
import type { AutoCompleteThemeVars } from 'naive-ui/es/auto-complete/styles'
import type { DrawerThemeVars } from 'naive-ui/es/drawer/styles'
import type { CheckboxThemeVars } from 'naive-ui/es/checkbox/styles'
import type { BreadcrumbThemeVars } from 'naive-ui/es/breadcrumb/styles'
import type { CarouselThemeVars } from 'naive-ui/es/carousel/styles'
import type { TooltipThemeVars } from 'naive-ui/es/tooltip/styles'
import type { PaginationThemeVars } from 'naive-ui/es/pagination/styles'
import type { MessageThemeVars } from 'naive-ui/es/message/styles'

import type { BasicPalette } from '@theme/theme.types'
import { DEFAULT_LIGHT_PALETTE } from '@theme/light.const'
import { exportObjectToGlobalCssProperties } from '@theme/theme.utils'

// the only mutable object. everything else is derived from this and not alterable by the user
export const lightPalette = ref<BasicPalette>({ ...DEFAULT_LIGHT_PALETTE })
exportObjectToGlobalCssProperties(lightPalette.value)

export const lightThemeOverrides = computed(() => {
  const common: ThemeCommonVars = {
    name: 'common',
    actionColor: 'rgba(250, 250, 252, 0.99)',
    avatarColor: 'rgba(204, 204, 204, 0.98)',
    baseColor: lightPalette.value.base,
    bodyColor: '#FFFFFFee',
    borderColor: 'rgba(216, 216, 220, 1)',
    borderRadius: '4px',
    borderRadiusSmall: '4px',
    boxShadow1: lightPalette.value.boxShadow,
    boxShadow2: lightPalette.value.boxShadow2,
    boxShadow3: lightPalette.value.boxShadow3,
    buttonColor2: 'rgba(57, 64, 71, 0.06)',
    buttonColor2Hover: 'rgba(57, 62, 67, 0.09)',
    buttonColor2Pressed: 'rgba(59, 65, 71, 0.14)',
    cardColor: lightPalette.value.primary6,
    clearColor: 'rgba(183, 180, 180, 1)',
    clearColorHover: 'rgba(140, 138, 138, 1)',
    clearColorPressed: 'rgba(167, 163, 163, 1)',
    closeColorHover: 'rgba(0, 0, 0, 0.14)',
    closeColorPressed: 'rgba(0, 0, 0, 0.17)',
    closeIconColor: 'rgba(99, 99, 99, 1)',
    closeIconColorHover: 'rgba(94, 91, 91, 1)',
    closeIconColorPressed: 'rgba(91, 87, 87, 1)',
    codeColor: lightPalette.value.info2,
    cubicBezierEaseIn: '0',
    cubicBezierEaseInOut: '0',
    cubicBezierEaseOut: '0',
    dividerColor: 'rgba(225, 225, 235, 1)',
    errorColor: lightPalette.value.error,
    errorColorHover: `${lightPalette.value.error}BD`,
    errorColorPressed: `${lightPalette.value.error}BD`,
    errorColorSuppl: '#E85B72FF',
    fontFamily: "'Roboto', sans-serif",
    fontFamilyMono: "'Roboto', sans-serif",
    fontSize: '14px',
    fontSizeHuge: '16px',
    fontSizeLarge: '15px',
    fontSizeMedium: '14px',
    fontSizeMini: '12px',
    fontSizeSmall: '14px',
    fontSizeTiny: '12px',
    fontWeight: '400',
    fontWeightStrong: '500',
    heightHuge: '46px',
    heightLarge: '40px',
    heightMedium: '36px',
    heightMini: '16px',
    heightSmall: '28px',
    heightTiny: '22px',
    hoverColor: 'rgba(243, 243, 245, 0.99)',
    iconColor: 'rgba(183, 179, 179, 1)',
    iconColorDisabled: lightPalette.value.primary4,
    iconColorHover: 'rgba(135, 133, 133, 1)',
    iconColorPressed: 'rgba(167, 163, 163, 1)',
    infoColor: lightPalette.value.info,
    infoColorHover: lightPalette.value.info,
    infoColorPressed: lightPalette.value.primary3,
    infoColorSuppl: lightPalette.value.info,
    inputColor: 'rgba(255, 255, 255, 0.99)',
    inputColorDisabled: 'rgba(250, 250, 252, 0.98)',
    invertedColor: 'rgba(0, 20, 40, 0.99)',
    lineHeight: '1.6',
    modalColor: lightPalette.value.primary6,
    opacity1: '0.82',
    opacity2: '0.72',
    opacity3: '0.38',
    opacity4: '0.24',
    opacity5: '0.18',
    opacityDisabled: '0.5',
    placeholderColor: 'rgba(184, 176, 176, 1)',
    placeholderColorDisabled: 'rgba(198, 194, 194, 1)',
    popoverColor: lightPalette.value.base,
    pressedColor: lightPalette.value.primary6,
    primaryColor: lightPalette.value.primary,
    primaryColorHover: `${lightPalette.value.primary}24`,
    primaryColorPressed: `${lightPalette.value.primary}14`,
    primaryColorSuppl: `${lightPalette.value.primary}E3`,
    progressRailColor: 'rgba(255, 255, 255, 0.13)',
    railColor: 'rgba(255, 255, 255, 0.13)',
    scrollbarBorderRadius: '5px',
    scrollbarColor: 'rgba(0, 0, 0, 0.26)',
    scrollbarColorHover: 'rgba(0, 0, 0, 0.41)',
    scrollbarHeight: '5px',
    scrollbarWidth: '5px',
    successColor: lightPalette.value.success,
    successColorHover: '#64A020FF',
    successColorPressed: lightPalette.value.success,
    successColorSuppl: lightPalette.value.success,
    tabColor: 'rgba(247, 247, 250, 0.99)',
    tableColor: '#FFFFFFee',
    tableColorHover: 'rgba(0, 0, 100, 0.04)',
    tableColorStriped: 'rgba(0, 0, 100, 0.04)',
    tableHeaderColor: 'rgba(250, 250, 252, 0.99)',
    tagColor: lightPalette.value.primary5,
    textColor1: lightPalette.value.text,
    textColor2: lightPalette.value.text,
    textColor3: lightPalette.value.text2,
    textColorBase: '#000000',
    textColorDisabled: 'rgba(180, 178, 178, 1)',
    warningColor: lightPalette.value.warning,
    warningColorHover: '#E5B002FF',
    warningColorPressed: '#88550EFF',
    warningColorSuppl: '#855815FF',
  }

  const Button: Partial<ButtonThemeVars> = {
    border: '1px solid transparent',
    borderDisabled: '1px sold rgb(158,158,158)',
    borderFocus: '1px solid transparent',
    borderHover: '1px solid transparent',
    borderPressed: '1px solid transparent',
    borderRadiusLarge: '4px',
    borderRadiusMedium: '4px',
    borderRadiusSmall: '4px',
    borderRadiusTiny: '2px',
    color: lightPalette.value.primary6,
    colorDisabled: '#E0E0E0',
    colorDisabledPrimary: lightPalette.value.primary,
    colorFocus: lightPalette.value.primary5,
    colorFocusPrimary: lightPalette.value.primary,
    colorHover: lightPalette.value.primary5,
    colorHoverPrimary: `${lightPalette.value.primary}F0`,
    colorPressed: lightPalette.value.primary5,
    colorPressedPrimary: lightPalette.value.primary,
    colorPrimary: lightPalette.value.primary, //secondary/secondary-default
    colorQuaternary: lightPalette.value.base,
    colorQuaternaryHover: lightPalette.value.primary6,
    colorQuaternaryPressed: `${lightPalette.value.primary5}75`,
    colorSecondary: lightPalette.value.primary6,
    colorSecondaryHover: lightPalette.value.primary5,
    colorSecondaryPressed: lightPalette.value.primary5,
    colorTertiary: 'rgba(46, 51, 56, .05)',
    colorTertiaryHover: 'rgba(46, 51, 56, .09)',
    fontSizeLarge: '14px',
    fontSizeMedium: '14px',
    fontSizeSmall: '14px',
    fontSizeTiny: '12px',
    fontWeight: '500',
    heightLarge: '44px',
    heightMedium: '36px',
    heightSmall: '28px',
    heightTiny: '20px',
    iconMarginLarge: '8px',
    iconMarginMedium: '8px',
    iconMarginSmall: '8px',
    iconMarginTiny: '8px',
    iconSizeLarge: '24px',
    iconSizeMedium: '20px',
    iconSizeSmall: '16px',
    iconSizeTiny: '14px',
    opacityDisabled: '0.5',
    paddingLarge: '0 16px',
    paddingMedium: '0 16px',
    paddingRoundLarge: '0 16px',
    paddingRoundMedium: '0 16px',
    paddingRoundSmall: '0 16px',
    paddingRoundTiny: '0 8px',
    paddingSmall: '0 16px',
    paddingTiny: '0 8px',
    rippleColor: lightPalette.value.primary,
    rippleDuration: '0',
    textColor: lightPalette.value.primary,
    textColorDisabled: '#9E9E9Eff',
    textColorFocus: lightPalette.value.primary,
    textColorGhost: lightPalette.value.primary,
    textColorGhostDisabled: '#9E9E9EFF',
    textColorGhostFocus: lightPalette.value.primary,
    textColorGhostFocusPrimary: lightPalette.value.primary,
    textColorGhostHover: lightPalette.value.primary,
    textColorGhostHoverPrimary: lightPalette.value.primary2,
    textColorGhostPressed: lightPalette.value.primary,
    textColorGhostPressedPrimary: lightPalette.value.primary2,
    textColorHover: lightPalette.value.primary,
    textColorPressed: lightPalette.value.primary,
    textColorPrimary: lightPalette.value.base,
    textColorTertiary: lightPalette.value.primary2,
    textColorText: lightPalette.value.primary,
    textColorTextDisabled: '#9E9E9Eff',
    textColorTextDisabledPrimary: lightPalette.value.base,
    textColorTextFocus: lightPalette.value.primary,
    textColorTextHover: lightPalette.value.primary2,
    textColorTextHoverPrimary: lightPalette.value.primary,
    textColorTextPressed: lightPalette.value.primary2,
    textColorTextPressedPrimary: lightPalette.value.primary,
  }

  const Input: Partial<InputThemeVars> = {
    border: `1px solid ${lightPalette.value.text3}`,
    borderRadius: '4px',
    boxShadowFocus: '0',
    boxShadowFocusError: '0',
    boxShadowFocusWarning: '0',
    clearSize: '24px',
    fontSizeMedium: '16px',
    heightLarge: '56px',
    iconColor: lightPalette.value.text,
    iconSize: '20px',
    paddingLarge: '12px',
    paddingMedium: '12px',
    placeholderColor: '#9E9E9E',
    textColor: lightPalette.value.text,
  }

  const Tag: Partial<TagThemeVars> = {
    border: `1px solid ${lightPalette.value.primary}`,
    borderError: `1px solid ${lightPalette.value.error}`,
    borderInfo: `1px solid ${lightPalette.value.info}`,
    borderPrimary: `1px solid ${lightPalette.value.primary}`,
    borderRadius: '2px',
    borderSuccess: `1px solid ${lightPalette.value.success}`,
    borderWarning: `1px solid ${lightPalette.value.warning}`,
    closeBorderRadius: '2px',
    closeColorHover: lightPalette.value.primary5,
    closeColorHoverError: lightPalette.value.error3,
    closeColorHoverInfo: lightPalette.value.info3,
    closeColorHoverPrimary: lightPalette.value.primary6,
    closeColorHoverSuccess: lightPalette.value.success3,
    closeColorHoverWarning: lightPalette.value.warning3,
    closeColorPressed: lightPalette.value.primary2,
    closeColorPressedError: lightPalette.value.error3,
    closeColorPressedInfo: lightPalette.value.info3,
    closeColorPressedPrimary: lightPalette.value.primary6,
    closeColorPressedSuccess: lightPalette.value.success3,
    closeColorPressedWarning: lightPalette.value.warning3,
    closeIconColor: lightPalette.value.primary,
    closeIconColorError: lightPalette.value.error2,
    closeIconColorHover: lightPalette.value.primary,
    closeIconColorHoverError: lightPalette.value.error2,
    closeIconColorHoverInfo: lightPalette.value.info2,
    closeIconColorHoverPrimary: lightPalette.value.primary,
    closeIconColorHoverSuccess: lightPalette.value.success2,
    closeIconColorHoverWarning: lightPalette.value.warning2,
    closeIconColorInfo: lightPalette.value.info2,
    closeIconColorPressed: lightPalette.value.primary,
    closeIconColorPressedError: lightPalette.value.error2,
    closeIconColorPressedInfo: lightPalette.value.info2,
    closeIconColorPressedPrimary: lightPalette.value.primary,
    closeIconColorPressedSuccess: lightPalette.value.success2,
    closeIconColorPressedWarning: lightPalette.value.warning2,
    closeIconColorPrimary: lightPalette.value.primary,
    closeIconColorSuccess: lightPalette.value.success2,
    closeIconColorWarning: lightPalette.value.warning2,
    closeIconSizeLarge: '16px',
    closeIconSizeMedium: '14px',
    closeIconSizeSmall: '12px',
    closeIconSizeTiny: '10px',
    closeMargin: '0 0 0 4px',
    closeSizeLarge: '16px',
    closeSizeMedium: '14px',
    closeSizeSmall: '12px',
    closeSizeTiny: '10px',
    color: lightPalette.value.primary6,
    colorBordered: lightPalette.value.primary6,
    colorBorderedError: lightPalette.value.error,
    colorBorderedInfo: lightPalette.value.info,
    colorBorderedPrimary: lightPalette.value.primary6,
    colorBorderedSuccess: lightPalette.value.success,
    colorBorderedWarning: lightPalette.value.warning,
    colorChecked: lightPalette.value.primary,
    colorCheckedHover: lightPalette.value.primary2,
    colorCheckedPressed: lightPalette.value.primary,
    colorError: lightPalette.value.error,
    colorHoverCheckable: lightPalette.value.primary6,
    colorInfo: lightPalette.value.info,
    colorPrimary: lightPalette.value.primary6,
    colorSuccess: lightPalette.value.success,
    colorWarning: lightPalette.value.warning,
    fontSizeLarge: '15px',
    fontSizeMedium: '14px',
    fontSizeSmall: '12px',
    fontSizeTiny: '10px',
    fontWeightStrong: '500',
    heightLarge: '32px',
    heightMedium: '28px',
    heightSmall: '22px',
    heightTiny: '16px',
    opacityDisabled: '0.25',
    padding: '8px',
    textColor: lightPalette.value.primary,
    textColorCheckable: lightPalette.value.primary,
    textColorChecked: lightPalette.value.primary6,
    textColorError: lightPalette.value.error2,
    textColorHoverCheckable: lightPalette.value.primary,
    textColorInfo: lightPalette.value.info2,
    textColorPressedCheckable: lightPalette.value.primary,
    textColorPrimary: lightPalette.value.primary,
    textColorSuccess: lightPalette.value.success2,
    textColorWarning: lightPalette.value.warning2,
  }
  const Dropdown: Partial<DropdownThemeVars> = {
    borderRadius: '4px',
    color: lightPalette.value.base,
    dividerColor: '#E0E0E0',
    fontSizeHuge: '16px',
    fontSizeLarge: '16px',
    fontSizeMedium: '14px',
    fontSizeSmall: '14px',
    groupHeaderTextColor: '#424242',
    optionColorActive: lightPalette.value.primary5,
    optionColorHover: lightPalette.value.primary6,
    optionHeightMedium: '24px',
    optionIconPrefixWidthMedium: '16px',
    optionIconSizeHuge: '16px',
    optionIconSizeLarge: '16px',
    optionIconSizeMedium: '16px',
    optionIconSizeSmall: '14px',
    optionTextColor: lightPalette.value.text,
    optionTextColorHover: lightPalette.value.primary,
    padding: '8px 4px',
    prefixColor: '#0000008A',
    suffixColor: '#0000008A',
  }

  const Popover: Partial<PopoverThemeVars> = {
    boxShadow: '4px 2px 24px rgba(73, 68, 68, 0.1)',
    borderRadius: '8px',
    padding: '8px',
  }

  const Rate: Partial<RateThemeVars> = {
    itemColor: lightPalette.value.primary4,
    itemColorActive: lightPalette.value.primary4,
    sizeMedium: '16px',
  }

  const Modal: Partial<ModalThemeVars> = {
    boxShadow: '4px 2px 24px rgba(73, 68, 68, 0.1)',
    color: lightPalette.value.base,
    textColor: lightPalette.value.text,
  }

  const Card: Partial<CardThemeVars> = {
    borderRadius: '4px',
    borderColor: '#F5F5F5',
    closeColorHover: '#FAFAFCFF',
    closeColorPressed: '#EFEFF5FF',
    closeIconColor: lightPalette.value.primary,
    closeIconColorHover: lightPalette.value.primary4,
    closeIconColorPressed: lightPalette.value.primary,
    closeIconSize: '20px',
    closeSize: '24px',
    color: lightPalette.value.base,
    colorEmbedded: lightPalette.value.primary6,
    colorEmbeddedModal: lightPalette.value.primary6,
    colorEmbeddedPopover: lightPalette.value.primary6,
    colorModal: lightPalette.value.base,
    fontSizeHuge: '16px',
    fontSizeLarge: '16px',
    fontSizeMedium: '14px',
    fontSizeSmall: '14px',
    lineHeight: '24px',
    paddingHuge: '32px',
    paddingLarge: '24px',
    paddingMedium: '16px',
    paddingSmall: '8px',
    textColor: lightPalette.value.text,
    titleFontSizeHuge: '20px',
    titleFontSizeLarge: '20px',
    titleFontSizeMedium: '18px',
    titleFontSizeSmall: '15px',
    titleTextColor: lightPalette.value.text,
  }

  const Form: Partial<FormThemeVars> = {
    asteriskColor: lightPalette.value.error,
    labelFontSizeTopMedium: '16px',
    labelPaddingVertical: '0 0 8px',
    labelTextColor: lightPalette.value.text,
    lineHeight: '24px',
  }

  const Select: Partial<SelectThemeVars & NonNullable<SelectProps['themeOverrides']>> = {
    peers: {
      InternalSelection: {
        paddingSingle: '0 30px 0 16px',
        border: `1px solid ${lightPalette.value.text3}`,
        borderActive: 'none',
        borderHover: 'none',
        borderFocus: 'none',
        arrowColor: lightPalette.value.primary,
        boxShadowActive: '0',
        boxShadowActiveError: '0',
        boxShadowActiveWarning: '0',
        boxShadowFocus: '0',
        boxShadowFocusError: '0',
        boxShadowFocusWarning: '0',
        boxShadowHover: '0',
        boxShadowHoverError: '0',
        boxShadowHoverWarning: '0',
        color: 'transparent',
        colorActive: `${lightPalette.value.primary}2B`,
        textColor: lightPalette.value.primary,
      },
      InternalSelectMenu: {
        color: lightPalette.value.base,
        optionFontSizeSmall: '0 16px',
        paddingSmall: '8px',
        optionTextColorPressed: '#424242',
        optionColorActive: lightPalette.value.primary5,
        optionColorActivePending: lightPalette.value.primary5,
      },
    },
  }

  const AutoComplete: Partial<AutoCompleteThemeVars & NonNullable<AutoCompleteProps['themeOverrides']>> = {
    peers: {
      InternalSelectMenu: {
        color: lightPalette.value.base,
        optionFontSizeSmall: '0 16px',
        paddingSmall: '8px',
        optionTextColorPressed: '#424242',
        optionColorActive: lightPalette.value.primary5,
        optionColorActivePending: lightPalette.value.primary5,
      },
    },
  }

  const InternalSelection: Partial<InternalSelectionThemeVars> = {
    border: `1px solid ${lightPalette.value.text3}`,
    caretColor: lightPalette.value.text,
    fontSizeMedium: '16px',
    textColor: lightPalette.value.text,
  }

  const DataTable: Partial<DataTableThemeVars & NonNullable<DataTableProps['themeOverrides']>> = {
    thPaddingSmall: '8px',
    thPaddingMedium: '12px',
    thPaddingLarge: '16px',
    tdPaddingSmall: '8px',
    tdPaddingMedium: '12px',
    tdPaddingLarge: '16px',
    sorterSize: '14px',
    resizableSize: '2px',
    resizableContainerSize: '8px',
    filterSize: '14px',
    paginationMargin: '12px 0 0 0 ',
    emptyPadding: '48px 0',
    actionPadding: '8px 12px',
    actionButtonMargin: '0 8px 0 0',
    actionDividerColor: lightPalette.value.primary6,
    borderRadius: '4px',
    fontSizeSmall: '12px',
    fontSizeMedium: '14px',
    fontSizeLarge: '16px',
    borderColor: lightPalette.value.primary6,
    tdColorHover: lightPalette.value.primary6_1,
    thColor: lightPalette.value.base,
    tdColor: lightPalette.value.base,
    thColorHover: lightPalette.value.primary6_1,
    tdTextColor: lightPalette.value.text,
    thTextColor: lightPalette.value.text,
    thFontWeight: '500',
    thIconColor: lightPalette.value.primary4,
    thIconColorActive: lightPalette.value.primary,
    thButtonColorHover: lightPalette.value.primary4,
    peers: {
      Pagination: {
        itemSizeLarge: '48px',
        itemFontSizeLarge: '16px',
        buttonIconSizeLarge: '24px',
      },
    },
  }

  const Collapse: Partial<CollapseThemeVars> = {
    arrowColor: lightPalette.value.primary,
    dividerColor: lightPalette.value.primary5,
    fontSize: '14px',
    textColor: '#424242FF',
    titleFontSize: '15px',
    titleFontWeight: '700',
    titleTextColor: lightPalette.value.text,
  }

  const Layout: Partial<LayoutThemeVars> = {
    color: lightPalette.value.primary6,
    headerColor: lightPalette.value.base,
  }

  const Divider: Partial<DividerThemeVars> = {
    color: lightPalette.value.primary5,
    textColor: lightPalette.value.text,
  }

  const Icon: Partial<IconThemeVars> = {
    color: lightPalette.value.primary,
  }

  const IconWrapper: Partial<IconWrapperThemeVars> = {
    color: lightPalette.value.primary6,
    iconColor: lightPalette.value.primary,
  }

  const PageHeader: Partial<PageHeaderThemeVars> = {
    backColor: lightPalette.value.primary,
    backColorHover: lightPalette.value.primary2,
    backColorPressed: lightPalette.value.primary3,
    backSize: '22px',
    fontSize: '14px',
    subtitleTextColor: '#424242FF',
    titleFontSize: '20px',
    titleTextColor: lightPalette.value.text,
  }

  const Typography: Partial<TypographyThemeVars> = {
    blockquoteFontSize: '14px',
    blockquoteLineHeight: '2',
    blockquotePrefixColor: lightPalette.value.primary5,
    blockquoteTextColor: lightPalette.value.text2,
    codeBorderRadius: '4px',
    codeTextColor: lightPalette.value.primary2,
    headerFontSize1: '34px',
    headerFontSize2: '23px',
    headerFontSize3: '20px',
    headerFontSize4: '16px',
    headerFontSize5: '15px',
    headerFontSize6: '15px',
    headerFontWeight: '500',
    headerTextColor: lightPalette.value.text,
    hrColor: '#EFEFF5FF',
    liTextColor: lightPalette.value.text,
    pFontSize: '14px',
    pLineHeight: '1.8',
    pTextColor: lightPalette.value.text,
    pTextColor1Depth: lightPalette.value.text,
    pTextColor2Depth: lightPalette.value.text2,
    pTextColor3Depth: lightPalette.value.text3,
    textColor: lightPalette.value.text,
  }

  const Drawer: Partial<DrawerThemeVars> = {
    color: lightPalette.value.base,
    bodyPadding: '32px',
    boxShadow: '4px 2px 24px rgba(73, 68, 68, 0.1)',
  }

  const Checkbox: Partial<CheckboxThemeVars> = {
    colorChecked: lightPalette.value.primary,
    checkMarkColor: lightPalette.value.base,
    border: `1px solid ${lightPalette.value.primary}`,
    sizeSmall: '14px',
    sizeMedium: '14px',
    sizeLarge: '16px',
    fontSizeSmall: '14px',
    fontSizeMedium: '14px',
    fontSizeLarge: '16px',
    textColor: lightPalette.value.primary,
    borderFocus: `1px solid ${lightPalette.value.primary}`,
    boxShadowFocus: '0 0 0 0',
    borderChecked: `1px solid ${lightPalette.value.primary}`,
    checkMarkColorDisabled: lightPalette.value.primary5,
    checkMarkColorDisabledChecked: lightPalette.value.primary5,
    borderDisabled: `1px solid ${lightPalette.value.primary5}`,
    borderDisabledChecked: `1px solid ${lightPalette.value.primary5}`,
    textColorDisabled: lightPalette.value.primary5,
    labelPadding: '0 8px',
    labelLineHeight: '1.6',
    labelFontWeight: '500',
  }

  const Breadcrumb: Partial<BreadcrumbThemeVars> = {
    itemTextColor: lightPalette.value.primary3,
    itemTextColorHover: lightPalette.value.primary3,
    itemTextColorPressed: lightPalette.value.primary3,
    itemTextColorActive: lightPalette.value.primary,
    separatorColor: lightPalette.value.primary4,
    itemBorderRadius: '4px',
    itemLineHeight: '24px',
    fontWeightActive: '400',
    fontSize: '16px',
  }

  const Carousel: Partial<CarouselThemeVars> = {
    dotSize: '8px',
    dotColor: lightPalette.value.primary5,
    dotColorActive: lightPalette.value.primary,
  }

  const Tooltip: Partial<TooltipThemeVars> = {
    padding: `0px 16px`,
    color: lightPalette.value.primary2,
    borderRadius: '4px',
  }
  const Pagination: Partial<PaginationThemeVars> = {
    itemSizeLarge: '44px',
    buttonIconSizeLarge: '24px',
    buttonColor: lightPalette.value.primary6,
    buttonColorHover: lightPalette.value.primary5,
    buttonBorder: '0px',
    itemBorderDisabled: '0px',
    buttonIconColor: lightPalette.value.primary,
    itemFontSizeLarge: '16px',
  }

  const messageBoxShadow = '1px 3px 8px 0 rgba(0, 0, 0, 0.18)'

  const Message: Partial<MessageThemeVars> = {
    color: lightPalette.value.primary6,
    colorError: lightPalette.value.primary6,
    colorInfo: lightPalette.value.primary6,
    colorWarning: lightPalette.value.primary6,
    colorSuccess: lightPalette.value.primary6,
    colorLoading: lightPalette.value.primary6,
    boxShadow: messageBoxShadow,
    boxShadowError: messageBoxShadow,
    boxShadowInfo: messageBoxShadow,
    boxShadowWarning: messageBoxShadow,
    boxShadowSuccess: messageBoxShadow,
    boxShadowLoading: messageBoxShadow,
  }

  return {
    common,
    Button,
    Input,
    Tag,
    Dropdown,
    Popover,
    Rate,
    Modal,
    Card,
    Form,
    Select,
    AutoComplete,
    InternalSelection,
    DataTable,
    Layout,
    Collapse,
    Divider,
    Icon,
    IconWrapper,
    PageHeader,
    Typography,
    Drawer,
    Checkbox,
    Breadcrumb,
    Carousel,
    Tooltip,
    Pagination,
    Message,
  }
})
